import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, Divider, Modal, Button, Text } from "@mantine/core";
import { BookOpen, LogOut, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Notifications from "../notifications/notifications";
import "./header.css";
import { TbMessage } from "react-icons/tb";
import { GlobalContext } from "../../GlobalContext";
import { handleLogout as logoutUser } from "../../utils/auth";

const Header = () => {
  const {
    userData,
    setUserData,
    unreadNotifications,
    isLoading,
    apiError,
    subscribe,
    fetchUserData,
  } = useContext(GlobalContext);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const LOGOUT_API_ENDPOINT = "/wp-json/dbsb/v1/user/logout";

  useEffect(() => {
    const unsubscribe = subscribe((eventType) => {
      if (
        eventType === "USER_DATA_UPDATED" ||
        eventType === "NOTIFICATIONS_UPDATED"
      ) {
        fetchUserData();
      }
    });
    return unsubscribe;
  }, [subscribe, fetchUserData]);

  const handleLogout = () => {
    setLogoutModalOpen(true);
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    // Keep modal open to show loading state
    
    try {
      // Use the utility function to handle logout
      await logoutUser(setUserData, navigate);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, the logout function should still redirect
    }
    
    // This line won't be reached because logoutUser redirects the page
    // But if it does, keep the loading state
  };

  const cancelLogout = () => {
    setLogoutModalOpen(false);
  };

  const isResourcesActive = location.pathname === "/resources";
  const isPatient = userData.role === "patient";
  const isNotVerified = userData.verificationStatus !== "Verified";

  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95 },
  };

  const dropdownVariants = {
    hidden: { opacity: 0, scale: 0.9, y: -10 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
    exit: { opacity: 0, scale: 0.9, y: -10 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
    exit: { opacity: 0, scale: 0.9 },
  };

  return (
    <motion.div
      className="header-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="header-right" variants={containerVariants}>
        <motion.div
          className="header-notifications-icon-container"
          variants={itemVariants}
        >
          {unreadNotifications > 0 && (
            <p className="notifications-count">{unreadNotifications}</p>
          )}
          <Notifications size={20} color="black" className="header-icon" />
        </motion.div>
        {isPatient || !isNotVerified ? (
          <motion.button
            className={`header-resources-button ${
              isResourcesActive ? "active" : ""
            }`}
            onClick={() => navigate("/resources")}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <svg
              className="header-icon header-resources-icon"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M13.1677 1.84207C12.826 1.5004 12.2344 1.73373 12.2344 2.20873V5.11707C12.2344 6.33373 13.2677 7.34207 14.526 7.34207C15.3177 7.3504 16.4177 7.3504 17.3594 7.3504C17.8344 7.3504 18.0844 6.79207 17.751 6.45873C16.551 5.2504 14.401 3.0754 13.1677 1.84207Z" />
              <path d="M17.0859 8.48906H14.6776C12.7026 8.48906 11.0943 6.88073 11.0943 4.90573V2.4974C11.0943 2.03906 10.7193 1.66406 10.2609 1.66406H6.7276C4.16094 1.66406 2.08594 3.33073 2.08594 6.30573V13.6891C2.08594 16.6641 4.16094 18.3307 6.7276 18.3307H13.2776C15.8443 18.3307 17.9193 16.6641 17.9193 13.6891V9.3224C17.9193 8.86406 17.5443 8.48906 17.0859 8.48906ZM9.58594 14.7891H6.2526C5.91094 14.7891 5.6276 14.5057 5.6276 14.1641C5.6276 13.8224 5.91094 13.5391 6.2526 13.5391H9.58594C9.9276 13.5391 10.2109 13.8224 10.2109 14.1641C10.2109 14.5057 9.9276 14.7891 9.58594 14.7891ZM11.2526 11.4557H6.2526C5.91094 11.4557 5.6276 11.1724 5.6276 10.8307C5.6276 10.4891 5.91094 10.2057 6.2526 10.2057H11.2526C11.5943 10.2057 11.8776 10.4891 11.8776 10.8307C11.8776 11.1724 11.5943 11.4557 11.2526 11.4557Z" />
            </svg>
            Resources
          </motion.button>
        ) : (
          <motion.button
            className={`header-resources-button ${
              location.pathname === "/contact-form" ? "active" : ""
            }`}
            onClick={() => navigate("/contact-form")}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <TbMessage size={22} className="header-icon" />
            Contact us
          </motion.button>
        )}
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <motion.div
              className="header-profile-pic"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {isLoading ? (
                "N"
              ) : userData.profilePic ? (
                <img
                  src={userData.profilePic}
                  alt="Profile"
                  className="header-profile-img"
                  onError={() => {}}
                />
              ) : (
                userData.firstName.charAt(0).toUpperCase()
              )}
            </motion.div>
          </Menu.Target>
          <AnimatePresence>
            <Menu.Dropdown className="header-dropdown">
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <motion.div
                  className="header-dropdown-profile"
                  variants={itemVariants}
                >
                  <motion.div
                    className="header-dropdown-pic"
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 10 }}
                  >
                    {isLoading ? (
                      "N"
                    ) : userData.profilePic ? (
                      <img
                        src={userData.profilePic}
                        alt="Profile"
                        className="header-profile-img"
                        onError={() => {}}
                      />
                    ) : (
                      userData.firstName.charAt(0).toUpperCase()
                    )}
                  </motion.div>
                  <div className="header-dropdown-info">
                    <motion.div
                      className="header-dropdown-name"
                      variants={itemVariants}
                    >
                      {isLoading
                        ? "Loading..."
                        : `${userData.firstName} ${userData.lastName}`}
                    </motion.div>
                    <motion.div
                      className="header-dropdown-email"
                      variants={itemVariants}
                    >
                      {isLoading ? "Loading..." : userData.email}
                    </motion.div>
                  </div>
                </motion.div>
                <motion.div variants={itemVariants}>
                  <Divider />
                </motion.div>
                <Menu.Item
                  leftSection={
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.41406 6.30312C7.6724 3.30312 9.21406 2.07812 12.5891 2.07812H12.6974C16.4224 2.07812 17.9141 3.56979 17.9141 7.29479V12.7281C17.9141 16.4531 16.4224 17.9448 12.6974 17.9448H12.5891C9.23906 17.9448 7.6974 16.7365 7.4224 13.7865"
                        stroke="#FF2B02"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.499 10H3.01562"
                        stroke="#FF2B02"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M4.8776 7.21094L2.08594 10.0026L4.8776 12.7943"
                        stroke="#FF2B02"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  }
                  onClick={handleLogout}
                  className="header-dropdown-item"
                  component={motion.div}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <span className="logout-label-span">Log out</span>
                </Menu.Item>
              </motion.div>
            </Menu.Dropdown>
          </AnimatePresence>
        </Menu>
      </motion.div>
      <AnimatePresence>
        {logoutModalOpen && (
          <Modal
            opened={logoutModalOpen}
            onClose={cancelLogout}
            withCloseButton={false}
            centered
            size="sm"
            className="remove-modal"
            overlayProps={{ opacity: 0.55, blur: 3 }}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.h3 variants={itemVariants}>
                Are you sure you want to log out?
              </motion.h3>
              <motion.div
                className="modal-buttons"
                variants={containerVariants}
              >
                <Button
                  onClick={cancelLogout}
                  className="cancel-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={isLoggingOut}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmLogout}
                  className="remove-button"
                  component={motion.button}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Logging out...
                    </div>
                  ) : (
                    "Log out"
                  )}
                </Button>
              </motion.div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
      
      {/* CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </motion.div>
  );
};

export default Header;
